import React from 'react';
import { Bookmark, Clock3, Database, MapPinned, Search, Trash2, TriangleAlert } from 'lucide-react';
import { Button, cn } from '@daater/ui';
import type { Dataset, SavedReportItem } from '../api/types';

type SavedSearchesSectionProps = {
  reports: SavedReportItem[];
  datasets: Dataset[];
  currentDatasetId?: string;
  currentTableId?: string;
  title?: string;
  description?: string;
  loading?: boolean;
  openingReportId?: string | null;
  deletingReportId?: string | null;
  onOpen?: (report: SavedReportItem, fallbackDataset?: string) => Promise<void> | void;
  onDelete?: (report: SavedReportItem) => Promise<void> | void;
  showScopeFilter?: boolean;
  emptyMessage?: string;
  compact?: boolean;
};

type ScopeFilter = 'current' | 'all' | 'other';

const formatSavedDate = (value?: string) => {
  if (!value) return 'Sin fecha';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

export default function SavedSearchesSection({
  reports,
  datasets,
  currentDatasetId,
  currentTableId,
  title = 'Búsquedas guardadas',
  description = 'Tus búsquedas reutilizables y listas para abrir con un clic.',
  loading = false,
  openingReportId,
  deletingReportId,
  onOpen,
  onDelete,
  showScopeFilter = true,
  emptyMessage = 'Todavía no tienes búsquedas guardadas en este contexto.',
  compact = false,
}: SavedSearchesSectionProps) {
  const [scopeFilter, setScopeFilter] = React.useState<ScopeFilter>('all');
  const [searchValue, setSearchValue] = React.useState('');

  React.useEffect(() => {
    if (!currentTableId) {
      setScopeFilter('all');
    }
  }, [currentTableId]);

  const items = React.useMemo(() => {
    const datasetLookup = new Map(datasets.map(dataset => [dataset._id, dataset]));

    return reports
      .map(report => {
        const fallbackDataset =
          (typeof report.dataset === 'string' && report.dataset.trim()) || currentDatasetId || '';
        const dataset = datasetLookup.get(fallbackDataset);
        const table = dataset?.tables?.find(item => item._id === report.table);
        const tableName =
          table?.name ?? (typeof report.table === 'string' ? report.table : 'Tabla');
        const datasetName = (dataset?.name ?? fallbackDataset) || 'Base sin nombre';
        const section =
          typeof dataset?.section === 'string' && dataset.section.trim()
            ? dataset.section
            : 'General';
        const lastTouched = report.lastUpdated ?? report.createdAt;

        return {
          report,
          fallbackDataset,
          datasetName,
          tableName,
          section,
          available: Boolean(dataset && table),
          lastTouched,
        };
      })
      .sort((left, right) => {
        const leftTime = left.lastTouched ? new Date(left.lastTouched).getTime() : 0;
        const rightTime = right.lastTouched ? new Date(right.lastTouched).getTime() : 0;
        return rightTime - leftTime;
      });
  }, [reports, datasets, currentDatasetId]);

  const filteredItems = React.useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return items.filter(item => {
      if (currentTableId && scopeFilter === 'current' && item.report.table !== currentTableId) {
        return false;
      }

      if (currentTableId && scopeFilter === 'other' && item.report.table === currentTableId) {
        return false;
      }

      if (!normalizedSearch) return true;

      const haystack = [item.report.name, item.datasetName, item.tableName, item.section]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [currentTableId, items, scopeFilter, searchValue]);

  return (
    <section className="relative overflow-hidden rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(255,255,255,0.98))] p-5 shadow-[0_26px_60px_-40px_rgba(15,23,42,0.28)] sm:p-6">
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
            <Bookmark className="h-3.5 w-3.5" />
            Guardado en Daater Cloud
          </div>
          <div>
            <h2 className="text-xl font-semibold text-charcoal-grey sm:text-2xl">{title}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{description}</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {showScopeFilter && currentTableId ? (
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/85 p-1 shadow-sm">
              {(
                [
                  ['current', 'Tabla actual'],
                  ['all', 'Todas'],
                  ['other', 'Otras'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScopeFilter(value)}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-xs font-semibold transition',
                    scopeFilter === value
                      ? 'bg-slate-950 text-white shadow-[0_10px_24px_-16px_rgba(15,23,42,0.7)]'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          <label className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-3 py-2 shadow-sm">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
              placeholder="Buscar por nombre, base o tabla"
              className="w-full min-w-[220px] bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
            />
          </label>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 rounded-[24px] border border-dashed border-slate-200 bg-white/70 px-5 py-10 text-center text-sm text-slate-500">
          Cargando búsquedas guardadas...
        </div>
      ) : filteredItems.length ? (
        <div
          className={cn(
            'mt-5 grid gap-4',
            compact ? 'xl:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-3'
          )}
        >
          {filteredItems.map(
            ({ report, fallbackDataset, datasetName, tableName, section, available }) => {
              const isOpening = openingReportId === report._id;
              const isDeleting = deletingReportId === report._id;

              return (
                <article
                  key={report._id}
                  className={cn(
                    'group relative overflow-hidden rounded-[24px] border bg-white/90 p-4 shadow-[0_20px_50px_-36px_rgba(15,23,42,0.55)] transition duration-300',
                    available
                      ? 'border-slate-200/80 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[0_30px_60px_-36px_rgba(37,99,235,0.42)]'
                      : 'border-amber-200/80 bg-amber-50/70'
                  )}
                >
                  <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-primary/6 blur-2xl transition group-hover:bg-primary/10" />

                  <div className="relative flex h-full flex-col gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-2">
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          <Clock3 className="h-3.5 w-3.5" />
                          {formatSavedDate(report.lastUpdated ?? report.createdAt)}
                        </div>
                        <div>
                          <h3 className="line-clamp-2 text-lg font-semibold text-slate-950">
                            {report.name || 'Búsqueda guardada'}
                          </h3>
                          {!available ? (
                            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                              <TriangleAlert className="h-3.5 w-3.5" />
                              Contexto no disponible
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-primary" />
                        <span className="font-medium text-slate-800">{datasetName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPinned className="h-4 w-4 text-sky-500" />
                        <span>
                          {section} ·{' '}
                          <span className="font-medium text-slate-800">{tableName}</span>
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => void onOpen?.(report, fallbackDataset)}
                        disabled={!available || isOpening || isDeleting || !onOpen}
                        className="rounded-full bg-slate-950 px-4 text-white hover:bg-slate-800"
                      >
                        {isOpening ? 'Abriendo...' : 'Abrir'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void onDelete?.(report)}
                        disabled={isOpening || isDeleting || !onDelete}
                        className="rounded-full border-darkish-pink/25 bg-white/80 px-4 text-darkish-pink hover:bg-darkish-pink/5"
                      >
                        {isDeleting ? (
                          'Eliminando...'
                        ) : (
                          <>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Eliminar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </article>
              );
            }
          )}
        </div>
      ) : (
        <div className="mt-5 rounded-[24px] border border-dashed border-slate-200 bg-white/75 px-5 py-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bookmark className="h-6 w-6" />
          </div>
          <p className="mt-4 text-base font-medium text-slate-900">{emptyMessage}</p>
          <p className="mt-2 text-sm text-slate-500">
            Guarda una búsqueda para volver a abrirla sin reconstruir todos los filtros.
          </p>
        </div>
      )}
    </section>
  );
}
